"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";

interface SpecialChar {
  char: string;
  name: string;
}

type Strings = { [K in keyof typeof SPECIAL_CHARACTERS_STRINGS]: string };

interface CharCategory {
  id: string;
  label: keyof Strings;
  chars: SpecialChar[];
}

const CATEGORIES: CharCategory[] = [
  {
    id: "punctuation",
    label: "punctuation",
    chars: [
      { char: "—", name: "em dash" },
      { char: "–", name: "en dash" },
      { char: "…", name: "ellipsis" },
      { char: "«", name: "left guillemet double angle quote" },
      { char: "»", name: "right guillemet double angle quote" },
      { char: "¿", name: "inverted question mark" },
      { char: "¡", name: "inverted exclamation mark" },
      { char: "§", name: "section sign" },
      { char: "¶", name: "pilcrow paragraph sign" },
      { char: "†", name: "dagger" },
      { char: "‡", name: "double dagger" },
      { char: "•", name: "bullet" },
      { char: "·", name: "middle dot interpunct" },
    ],
  },
  {
    id: "arrows",
    label: "arrows",
    chars: [
      { char: "→", name: "right arrow" },
      { char: "←", name: "left arrow" },
      { char: "↑", name: "up arrow" },
      { char: "↓", name: "down arrow" },
      { char: "↔", name: "left right arrow" },
      { char: "⇒", name: "double right arrow implies" },
      { char: "⇐", name: "double left arrow" },
      { char: "↗", name: "up right diagonal arrow" },
      { char: "↘", name: "down right diagonal arrow" },
    ],
  },
  {
    id: "math",
    label: "math",
    chars: [
      { char: "±", name: "plus minus" },
      { char: "×", name: "multiplication sign times" },
      { char: "÷", name: "division sign" },
      { char: "≠", name: "not equal" },
      { char: "≈", name: "approximately equal" },
      { char: "≤", name: "less than or equal" },
      { char: "≥", name: "greater than or equal" },
      { char: "∞", name: "infinity" },
      { char: "√", name: "square root radical" },
      { char: "∑", name: "summation sigma" },
      { char: "π", name: "pi" },
      { char: "°", name: "degree sign" },
      { char: "µ", name: "micro sign mu" },
    ],
  },
  {
    id: "currency",
    label: "currency",
    chars: [
      { char: "€", name: "euro sign" },
      { char: "£", name: "pound sterling sign" },
      { char: "¥", name: "yen yuan sign" },
      { char: "¢", name: "cent sign" },
      { char: "₹", name: "indian rupee sign" },
      { char: "₩", name: "korean won sign" },
    ],
  },
  {
    id: "stars",
    label: "starsAndMarks",
    chars: [
      { char: "★", name: "black star filled" },
      { char: "☆", name: "white star outline" },
      { char: "✓", name: "check mark tick" },
      { char: "✗", name: "ballot x cross mark" },
      { char: "✦", name: "four pointed star sparkle" },
      { char: "♥", name: "heart" },
      { char: "♦", name: "diamond" },
    ],
  },
  {
    id: "legal",
    label: "legal",
    chars: [
      { char: "©", name: "copyright sign" },
      { char: "®", name: "registered trademark sign" },
      { char: "™", name: "trademark sign" },
    ],
  },
  {
    id: "accents",
    label: "accents",
    chars: [
      { char: "é", name: "e acute lowercase" },
      { char: "É", name: "e acute uppercase" },
      { char: "è", name: "e grave lowercase" },
      { char: "È", name: "e grave uppercase" },
      { char: "ê", name: "e circumflex lowercase" },
      { char: "Ê", name: "e circumflex uppercase" },
      { char: "ñ", name: "n tilde lowercase enye" },
      { char: "Ñ", name: "n tilde uppercase enye" },
      { char: "ü", name: "u umlaut diaeresis lowercase" },
      { char: "Ü", name: "u umlaut diaeresis uppercase" },
      { char: "ç", name: "c cedilla lowercase" },
      { char: "Ç", name: "c cedilla uppercase" },
      { char: "ö", name: "o umlaut diaeresis lowercase" },
      { char: "Ö", name: "o umlaut diaeresis uppercase" },
      { char: "å", name: "a ring lowercase" },
      { char: "Å", name: "a ring uppercase" },
      { char: "ø", name: "o slash stroke lowercase" },
      { char: "Ø", name: "o slash stroke uppercase" },
      { char: "æ", name: "ae ligature lowercase" },
      { char: "Æ", name: "ae ligature uppercase" },
      { char: "œ", name: "oe ligature lowercase" },
      { char: "Œ", name: "oe ligature uppercase" },
    ],
  },
  {
    id: "fractions",
    label: "fractions",
    chars: [
      { char: "½", name: "one half fraction" },
      { char: "⅓", name: "one third fraction" },
      { char: "¼", name: "one quarter fraction" },
      { char: "¾", name: "three quarters fraction" },
      { char: "⅔", name: "two thirds fraction" },
      { char: "⅛", name: "one eighth fraction" },
    ],
  },
];

const MAX_RECENT = 10;

function filterCategories(query: string): CharCategory[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return CATEGORIES;
  }
  return CATEGORIES.map((cat) => ({
    ...cat,
    chars: cat.chars.filter(
      (c) => c.name.includes(q) || c.char === query.trim()
    ),
  })).filter((cat) => cat.chars.length > 0);
}

/** English strings. A translated content file overrides these through `ui`. */
export const SPECIAL_CHARACTERS_STRINGS = {
  searchSymbolsByName: "Search symbols by name",
  recentlyCopied: "Recently copied",
  copied: "Copied",
  noSymbolsMatchYourSearch:
    'No symbols match your search. Try a shorter term like "dash" or "star".',
  searchPlaceholder: 'Try "em dash", "arrow", "euro", or "check"',
  copiedTitle: "Copied!",
  copyAgainTitle: "Copy again",
  copySymbolAriaLabel: "Copy {name}",
  punctuation: "Punctuation",
  arrows: "Arrows",
  math: "Math",
  currency: "Currency",
  starsAndMarks: "Stars & marks",
  legal: "Legal",
  accents: "Accents",
  fractions: "Fractions",
} as const;

export default function SpecialCharactersWidget() {
  const [query, setQuery] = useState("");
  const [lastCopied, setLastCopied] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>([]);

  const visibleCategories = filterCategories(query);

  function handleCopy(char: string) {
    writeToClipboard(char).then((copied) => {
      if (!copied) {
        return;
      }

      setLastCopied(char);
      setRecent((prev) => {
        const next = [char, ...prev.filter((c) => c !== char)];
        return next.slice(0, MAX_RECENT);
      });
      setTimeout(
        () => setLastCopied((current) => (current === char ? null : current)),
        1500
      );
    });
  }

  const strings = useToolUi(SPECIAL_CHARACTERS_STRINGS);

  return (
    <>
      {/* Search */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="char-search"
        >
          {strings.searchSymbolsByName}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="char-search"
          onChange={(e) => setQuery(e.target.value)}
          placeholder={strings.searchPlaceholder}
          type="search"
          value={query}
        />
      </div>
      {/* Recently copied */}
      {recent.length > 0 && (
        <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-4">
          <p className="font-medium text-foreground text-sm">
            {strings.recentlyCopied}
          </p>
          <div className="flex flex-wrap gap-2">
            {recent.map((char) => (
              <button
                className="min-w-10 rounded-lg border border-border bg-card px-3 py-2 text-foreground text-lg transition-colors hover:bg-muted"
                key={char}
                onClick={() => handleCopy(char)}
                title={
                  lastCopied === char
                    ? strings.copiedTitle
                    : strings.copyAgainTitle
                }
                type="button"
              >
                {lastCopied === char ? (
                  <span className="text-primary text-sm">{strings.copied}</span>
                ) : (
                  char
                )}
              </button>
            ))}
          </div>
        </section>
      )}
      {/* Symbol grid */}
      {visibleCategories.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {strings.noSymbolsMatchYourSearch}
        </p>
      )}
      {visibleCategories.map((cat) => (
        <section className="space-y-3" key={cat.id}>
          <h2 className="font-semibold text-foreground text-xl">
            {strings[cat.label]}
          </h2>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
            {cat.chars.map((c) => (
              <button
                aria-label={t(strings.copySymbolAriaLabel, { name: c.name })}
                className="flex h-14 items-center justify-center rounded-lg border border-border bg-card text-foreground text-xl transition-colors hover:border-primary hover:bg-primary/5"
                key={c.char}
                onClick={() => handleCopy(c.char)}
                title={c.name}
                type="button"
              >
                {lastCopied === c.char ? (
                  <span className="text-primary text-xs">{strings.copied}</span>
                ) : (
                  c.char
                )}
              </button>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
